/**
 * Écran de réservation de tournage
 * 
 * ARCHITECTURE :
 * - Utilise le hook useBookingFlow pour toute la logique métier
 * - Composé de sous-composants réutilisables (PlanSelector, DatePickerSection, TimeSlotGrid, BookingConfirmation)
 * - Gère uniquement la coordination entre les composants et les dialogues
 * 
 * FONCTIONNALITÉS :
 * - Sélection du plan (Présence, Émergence, Accélération)
 * - Calendrier avec disponibilités en temps réel
 * - Sélection de créneaux horaires
 * - Confirmation avec CGV
 * - Multi-hook (réservation préparation + tournage)
 * - Gestion des erreurs et retry
 * 
 * @param onRequestConcierge - Callback pour ouvrir la conciergerie
 * @param onDialogOpenChange - Callback pour notifier l'ouverture/fermeture de dialogues
 */

import { useState, useEffect, useRef } from 'react';
import { Sparkles, ChevronDown, MapPin, X } from 'lucide-react';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Calendar } from '../components/ui/calendar';
import { Spinner } from '../components/ui/spinner';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import { PreparationBookingScreen } from './PreparationBookingScreen';
import { BookingSuccessScreen } from './BookingSuccessScreen';
import { useBookingFlow } from '@/hooks/useBookingFlow';
import { BookingConfirmation } from '../components/scheduling/BookingConfirmation';
import { MultiHookModal } from '../components/modals/MultiHookModal';
import { formatSlotTime } from '@/services/integrations/cal';
import { logger } from '@/utils/logger';
import { cn } from '@/lib/utils';
import { getCurrentUser } from '@/services/data/users';
import { getBookingWithDirectorByUid, getPreparationBookingWithPBM } from '@/services/data/bookings';

// Format une tranche horaire (début - fin)
const formatTimeRange = (start: string, end: string): string => {
  return `${formatSlotTime(start)} - ${formatSlotTime(end)}`;
};

interface SchedulingScreenProps {
  onRequestConcierge?: () => void;
  onDialogOpenChange?: (isOpen: boolean) => void;
  onNavigateToCalendar?: () => void;
  hideHeader?: boolean;
}
const plans = [{
  value: 'presence',
  label: 'Présence',
  subtitle: '6 mois - 4 contenus/mois',
  duration: 30,
  price: '6 000€ HT',
  priceDetail: '1 000€ HT/mois',
  features: ['4 vidéos diffusées par mois', '24 vidéos au total sur 6 mois', 'Stratégie de contenu sur le long terme', 'Conciergerie & Support premium']
}, {
  value: 'emergence',
  label: 'Émergence',
  subtitle: '2 mois - 10 contenus/mois',
  duration: 30,
  price: '5 000€ HT',
  priceDetail: '2 500€ HT/mois',
  features: ['10 vidéos diffusées par mois', '20 vidéos au total sur 2 mois', 'Stratégie de lancement rapide', 'Conciergerie & Support premium']
}, {
  value: 'acceleration',
  label: 'Accélération',
  subtitle: '1 mois - 20 contenus/mois',
  duration: 30,
  price: '5 000€ HT',
  priceDetail: 'Programme intensif',
  features: ['20 vidéos diffusées en 1 mois', '5 vidéos par semaine', 'Stratégie de croissance accélérée', 'Conciergerie & Support premium']
}];
export function SchedulingScreen({
  onRequestConcierge,
  onDialogOpenChange,
  onNavigateToCalendar,
  hideHeader = false
}: SchedulingScreenProps) {
  // Utiliser le hook de booking flow
  const {
    userId,
    userEmail,
    userName,
    selectedPlan,
    shootingDurationMinutes,
    productId,
    selectedLocationName,
    selectedCity,
    availableCities,
    selectedDate,
    selectedSlot,
    datesWithAvailability,
    availableSlots,
    isLoadingSlots,
    isBooking,
    selectPlan,
    selectCity,
    selectDate,
    selectSlot,
    createBookingWithValidation,
    resetBooking
  } = useBookingFlow();

  // États locaux pour l'UI
  const calendarRef = useRef<HTMLDivElement>(null);
  const timeSlotsRef = useRef<HTMLDivElement>(null);
  const [isPlanSelectorOpen, setIsPlanSelectorOpen] = useState<boolean>(false);
  const [isCitySelectorOpen, setIsCitySelectorOpen] = useState<boolean>(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [showMultiHookDialog, setShowMultiHookDialog] = useState<boolean>(false);
  const [showCGVDialog, setShowCGVDialog] = useState<boolean>(false);
  const [showPreparationBooking, setShowPreparationBooking] = useState<boolean>(false);
  const [acceptedCGV, setAcceptedCGV] = useState<boolean>(false);
  const [showCGVWarning, setShowCGVWarning] = useState<boolean>(false);
  const [showBookingSuccess, setShowBookingSuccess] = useState<boolean>(false);
  const [confirmedShootingDate, setConfirmedShootingDate] = useState<string | undefined>();
  const [confirmedPreparationDate, setConfirmedPreparationDate] = useState<string | undefined | 'pending'>();
  const [bookingWithMultiHook, setBookingWithMultiHook] = useState<boolean | null>(null);
  const [directorName, setDirectorName] = useState<string | undefined>();
  const [directorAvatar, setDirectorAvatar] = useState<string | undefined>();
  const [pbmName, setPbmName] = useState<string | undefined>();
  const [pbmAvatar, setPbmAvatar] = useState<string | undefined>();
  const [shootingId, setShootingId] = useState<string | undefined>();
  const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());

  // Log pour déboguer l'affichage de BookingSuccess
  useEffect(() => {
    console.log('%c🔍 État BookingSuccess', 'color: #8b5cf6', { 
      showBookingSuccess, 
      confirmedShootingDate, 
      confirmedPreparationDate 
    });
  }, [showBookingSuccess, confirmedShootingDate, confirmedPreparationDate]);

  // Notifier App.tsx quand un dialogue est ouvert/fermé
  useEffect(() => {
    const isAnyDialogOpen = showConfirmDialog || showMultiHookDialog || showCGVDialog || showPreparationBooking;
    onDialogOpenChange?.(isAnyDialogOpen);
  }, [showConfirmDialog, showMultiHookDialog, showCGVDialog, showPreparationBooking, onDialogOpenChange]);

  // Auto-scroll vers le calendrier quand un plan est sélectionné
  useEffect(() => {
    if (selectedPlan && calendarRef.current) {
      setTimeout(() => {
        calendarRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 300);
    }
  }, [selectedPlan]);

  // Auto-scroll vers les créneaux quand une date est sélectionnée
  useEffect(() => {
    if (selectedDate && timeSlotsRef.current && !isLoadingSlots) {
      setTimeout(() => {
        timeSlotsRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [selectedDate, isLoadingSlots]);

  // Position calendar on the month of the first available date
  useEffect(() => {
    if (!selectedPlan || datesWithAvailability.size === 0) return;

    // Get the first available date
    const sortedDates = Array.from(datesWithAvailability).sort((a, b) => a.localeCompare(b));
    const firstAvailableDateStr = sortedDates[0];
    
    if (firstAvailableDateStr) {
      const firstAvailableDate = new Date(firstAvailableDateStr);
      // Set calendar to the month of the first available date
      setCalendarMonth(new Date(firstAvailableDate.getFullYear(), firstAvailableDate.getMonth(), 1));
    }
  }, [selectedPlan, datesWithAvailability]);

  const handlePlanSelect = (planValue: string) => {
    logger.info('Plan selected', {
      plan: planValue
    });
    selectPlan(planValue);
    setIsPlanSelectorOpen(false);
  };
  const handleSlotSelect = (slot: any) => {
    logger.info('Slot selected', {
      slot: slot.start
    });
    selectSlot(slot);
    setShowConfirmDialog(true);
  };
  const handleCloseConfirmDialog = () => {
    setShowConfirmDialog(false);
    setAcceptedCGV(false);
    setShowCGVWarning(false);
  };
  const handleConfirmBooking = (_withMultiHook: boolean) => {
    if (!acceptedCGV) {
      setShowCGVWarning(true);
      return;
    }

    setShowConfirmDialog(false);
    setShowMultiHookDialog(true);
  };

  const handleFinalBooking = async (withMultiHook: boolean) => {
    setBookingWithMultiHook(withMultiHook);
    
    console.log('%c🔵 DÉBUT handleFinalBooking', 'color: #3b82f6; font-weight: bold', { withMultiHook, selectedSlot: selectedSlot?.start });

    try {
      const result = await createBookingWithValidation(withMultiHook);
      console.log('%c📊 Résultat createBookingWithValidation', 'color: #3b82f6', { success: result.success, booking: result.booking });
      
      if (result.success && result.booking) {
        logger.info('Booking created successfully', {
          bookingId: result.booking.bookingId,
          withMultiHook,
        });

        // Toujours définir la date de tournage confirmée
        const shootingDate = selectedSlot?.start;
        if (shootingDate) {
          console.log('%c✅ Date de tournage confirmée', 'color: #10b981', { shootingDate });
          setConfirmedShootingDate(shootingDate);
        }
        
        
        // Récupérer les informations du staff depuis le résultat du booking
        console.log('%c🔍 Staff info from booking result:', 'color: #8b5cf6', {
          directorName: result.booking.directorName,
          directorAvatar: result.booking.directorAvatar,
          bookingUid: result.booking.bookingUid
        });
        
        if (result.booking.directorName) {
          setDirectorName(result.booking.directorName);
          setDirectorAvatar(result.booking.directorAvatar);
          console.log('%c👨‍💼 Director from booking result:', 'color: #3b82f6', result.booking.directorName);
        }
        
        // TOUJOURS récupérer depuis la DB pour être sûr (après un court délai pour laisser syncStaffFromCalBooking finir)
        if (result.booking.bookingUid) {
          console.log('%c⏳ Attente 1s puis récupération director depuis DB...', 'color: #f59e0b');
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Récupérer le booking avec les infos du staff via join
          const bookingResult = await getBookingWithDirectorByUid(result.booking.bookingUid);
          
          console.log('%c📊 Booking data from DB:', 'color: #8b5cf6', bookingResult.data);
          
          if (bookingResult.success && bookingResult.data?.team_film_directors) {
            const director = bookingResult.data.team_film_directors as any;
            const name = `${director.first_name} ${director.last_name}`;
            setDirectorName(name);
            setDirectorAvatar(director.avatar_url || undefined);
            console.log('%c✅ Director récupéré depuis DB:', 'color: #10b981', name);
          } else {
            console.warn('%c⚠️ Pas de director trouvé dans la DB', 'color: #ef4444', { bookingData: bookingResult.data });
          }
        }

        // Ne fermer le dialogue qu'après avoir décidé quoi afficher ensuite
        setShowMultiHookDialog(false);
        console.log('%c🔒 MultiHookDialog fermé', 'color: #3b82f6');

        // TOUJOURS afficher la réservation de préparation après un shooting
        // Le multi-hook change juste le produit mais pas le flow
        console.log('%c🟢 Affichage PreparationBooking (obligatoire)', 'color: #10b981; font-weight: bold');
        setConfirmedPreparationDate('pending');
        // Store shootingId for passing to PreparationBookingScreen
        setShootingId(result.booking.shootingId);
        setShowPreparationBooking(true);
        console.log('%c📊 État après booking', 'color: #3b82f6', { 
          showPreparationBooking: true, 
          showBookingSuccess: false,
          withMultiHook 
        });
      } else {
        console.error('%c❌ Échec de la réservation', 'color: #ef4444; font-weight: bold', { error: result.error });
        toast.error('Erreur lors de la réservation', {
          description: result.error || 'Une erreur est survenue',
        });
      }
    } catch (error) {
      console.error('%c❌ Exception dans handleFinalBooking', 'color: #ef4444; font-weight: bold', error);
      logger.error('Booking creation failed', error);
      toast.error('Erreur lors de la réservation');
    } finally {
      setBookingWithMultiHook(null);
      console.log('%c🔵 FIN handleFinalBooking', 'color: #3b82f6; font-weight: bold');
    }
  };
  const handlePreparationBookingSuccess = async (preparationSlot: string) => {
    console.log('%c🟣 DÉBUT handlePreparationBookingSuccess', 'color: #a855f7; font-weight: bold', { preparationSlot });
    console.log('%c📊 État avant mise à jour', 'color: #a855f7', { 
      confirmedShootingDate, 
      showPreparationBooking, 
      showBookingSuccess 
    });
    
    setConfirmedPreparationDate(preparationSlot);
    
    // Récupérer le nom du PBM depuis le booking de préparation
    try {
      const userResult = await getCurrentUser();
      
      if (userResult.success && userResult.data) {
        // Récupérer le booking de préparation le plus récent (order by created_at desc, limit 1)
        const prepBookingResult = await getPreparationBookingWithPBM(userResult.data.id, preparationSlot);
        
        if (!prepBookingResult.success) {
          console.error('%c❌ Erreur requête booking préparation', 'color: #ef4444', prepBookingResult.error);
        }
        
        // Utiliser directement les données jointes team_personal_brand_managers
        if (prepBookingResult.success && prepBookingResult.data?.team_personal_brand_managers) {
          const pbm = prepBookingResult.data.team_personal_brand_managers as any;
          const name = `${pbm.first_name} ${pbm.last_name}`;
          setPbmName(name);
          setPbmAvatar(pbm.avatar_url || undefined);
          console.log('%c👥 PBM récupéré depuis les données jointes:', 'color: #a855f7', { name, avatar: pbm.avatar_url });
        } else {
          console.warn('%c⚠️ Aucun PBM trouvé pour ce booking de préparation', 'color: #f59e0b', { 
            prepBooking: prepBookingResult.data, 
            personal_brand_manager_id: prepBookingResult.data?.personal_brand_manager_id 
          });
        }
      }
    } catch (error) {
      console.error('%c❌ Exception récupération PBM', 'color: #ef4444', error);
    }
    
    setShowPreparationBooking(false);
    console.log('%c🔒 PreparationBooking fermé', 'color: #a855f7');
    
    // Petit délai pour éviter le conflit entre la fermeture d'un dialogue et l'ouverture d'un autre
    setTimeout(() => {
      console.log('%c✅ BookingSuccess activé (après délai)', 'color: #10b981; font-weight: bold');
      setShowBookingSuccess(true);
      console.log('%c📊 État après mise à jour', 'color: #a855f7', { 
        confirmedPreparationDate: preparationSlot,
        showPreparationBooking: false, 
        showBookingSuccess: true 
      });
    }, 100);
    
    console.log('%c🟣 FIN handlePreparationBookingSuccess', 'color: #a855f7; font-weight: bold');
  };
  const handleBookingSuccessClose = () => {
    console.log('%c🔵 DÉBUT handleBookingSuccessClose', 'color: #3b82f6; font-weight: bold');
    setShowBookingSuccess(false);
    resetBooking();
    setConfirmedShootingDate(undefined);
    setConfirmedPreparationDate(undefined);
    console.log('%c✅ État réinitialisé', 'color: #10b981');
    
    // Naviguer vers le planning
    if (onNavigateToCalendar) {
      onNavigateToCalendar();
    }
    
    console.log('%c🔵 FIN handleBookingSuccessClose', 'color: #3b82f6; font-weight: bold');
  };
  const currentPlan = plans.find((plan) => plan.value === selectedPlan);
  const progressValue = selectedPlan ? (selectedDate ? (selectedSlot ? 100 : 66) : 33) : 0;
  return <div className="relative min-h-screen pb-32">
      <div className="w-full">
        <div className="relative z-10 px-4 pt-6">
          {/* Header - hidden when in sidebar */}
          {!hideHeader && (
            <div className="mb-6">
              <div className="flex items-center h-12">
                <h1 className="text-white text-2xl tracking-wide">Réserver un tournage</h1>
              </div>
            </div>
          )}

          <div className="max-w-lg mx-auto px-2">

            {/* Wrapper sections */}
            <div className="flex flex-col gap-6">


              {/* Section Localisation + Plan */}
              <div className="glass rounded-3xl p-6 shadow-xl flex flex-col gap-6">
                {/* Localisation (Ville) */}
                <div>
                  <label htmlFor="city-selector" className="text-sm text-white/60 mb-3 block">Ville</label>
                  <div className="glass border border-white/10 rounded-2xl">
                    {/* Header cliquable si plusieurs villes */}
                    <button
                      id="city-selector"
                      onClick={() => availableCities.length > 1 && setIsCitySelectorOpen(!isCitySelectorOpen)}
                      className={cn(
                        "w-full px-4 py-3 flex items-center gap-3 transition-colors rounded-2xl",
                        availableCities.length > 1 && "hover:bg-white/5 cursor-pointer"
                      )}
                      disabled={availableCities.length <= 1}
                    >
                      <MapPin className="h-5 w-5 text-white flex-shrink-0" />
                      <span className="text-base text-white leading-tight flex-1 text-left">
                        {selectedCity || 'Sélectionner une ville'}
                      </span>
                      {availableCities.length > 1 && (
                        <ChevronDown className={cn(
                          "h-5 w-5 text-white/50 transition-transform flex-shrink-0", 
                          isCitySelectorOpen && "rotate-180"
                        )} />
                      )}
                    </button>

                    {/* Options de villes */}
                    {isCitySelectorOpen && availableCities.length > 1 && (
                      <div className="border-t border-white/10">
                        {availableCities.map(cityOption => (
                          <button 
                            key={cityOption.city} 
                            onClick={() => {
                              selectCity(cityOption.city);
                              setIsCitySelectorOpen(false);
                            }} 
                            className={cn(
                              "w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-white/5 transition-colors border-b border-white/10 last:border-b-0", 
                              selectedCity === cityOption.city && "bg-primary/10"
                            )}
                          >
                            <MapPin className="h-4 w-4 text-white/50" />
                            <span className="text-base text-white">{cityOption.city}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Plan */}
                <div>
                  <label className="text-sm text-white/60 mb-3 block">Plan</label>
                  <div className="glass border border-white/10 rounded-2xl">
                    {/* Header */}
                    <button onClick={() => setIsPlanSelectorOpen(!isPlanSelectorOpen)} className="w-full px-4 py-3 flex items-start gap-3 hover:bg-white/5 transition-colors rounded-2xl">
                      <Sparkles className="h-5 w-5 text-white flex-shrink-0 mt-0.5" />
                      <div className="flex flex-col items-start gap-1 flex-1 min-w-0 text-left">
                        <span className="text-base text-white leading-tight">
                          {selectedPlan ? plans.find(p => p.value === selectedPlan)?.label : 'Sélectionner un plan'}
                        </span>
                        {selectedPlan && <span className="text-sm text-white/50 leading-tight">
                            {plans.find(p => p.value === selectedPlan)?.subtitle}
                          </span>}
                      </div>
                      <ChevronDown className={cn("h-5 w-5 text-white/50 transition-transform flex-shrink-0", isPlanSelectorOpen && "rotate-180")} />
                    </button>

                    {/* Options expandables */}
                    {isPlanSelectorOpen && <div className="border-t border-white/10">
                        {plans.map(plan => <button key={plan.value} onClick={() => {
                      handlePlanSelect(plan.value);
                    }} className={cn("w-full px-4 py-4 flex flex-col gap-2 text-left hover:bg-white/5 transition-colors border-b border-white/10 last:border-b-0", selectedPlan === plan.value && "bg-primary/10")}>
                            <div className="flex items-start justify-between gap-3">
                              <span className="text-base font-medium text-white flex-1 min-w-0">{plan.label}</span>
                              <span className="text-sm font-semibold text-white shrink-0">{plan.price}</span>
                            </div>
                            <span className="text-sm text-white/60">{plan.subtitle}</span>
                            <span className="text-xs text-white/40">{plan.priceDetail}</span>
                          </button>)}
                      </div>}
                  </div>
                </div>
              </div>

              {/* Section Calendrier */}
              {selectedPlan && <div ref={calendarRef} className="glass rounded-3xl p-6 shadow-xl relative min-h-[360px]">
                  {isLoadingSlots && (
                    <div className="absolute inset-0 bg-black/20 backdrop-blur-sm rounded-3xl flex items-center justify-center z-10">
                      <Spinner size="md" variant="default" label="Chargement des disponibilités" />
                    </div>
                  )}
                  <div className={cn(isLoadingSlots && "opacity-50 pointer-events-none")}>
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={selectDate}
                      month={calendarMonth}
                      onMonthChange={setCalendarMonth}
                      locale={fr}
                      disabled={date => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (date < today) return true;
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, "0");
                        const day = String(date.getDate()).padStart(2, "0");
                        const dateStr = `${year}-${month}-${day}`;
                        return !datesWithAvailability.has(dateStr);
                      }}
                      className="w-full pointer-events-auto"
                      classNames={{
                        months: "flex flex-col space-y-4 w-full",
                        month: "space-y-4 w-full",
                        caption: "flex justify-center pt-1 relative items-center",
                        caption_label: "text-sm font-medium text-white",
                        nav: "space-x-1 flex items-center",
                        nav_button: "h-7 w-7 bg-transparent p-0 text-white/50 hover:text-white transition-colors",
                        nav_button_previous: "absolute left-1",
                        nav_button_next: "absolute right-1",
                        table: "w-full border-collapse space-y-1",
                        head_row: "flex w-full justify-between",
                        head_cell: "text-white/40 rounded-md w-9 font-normal text-[0.8rem]",
                        row: "flex w-full mt-2 justify-between",
                        cell: "relative p-0 text-center text-sm w-9 h-9",
                        day: "h-9 w-9 p-0 rounded-md text-white/70 hover:bg-white/10 transition-all font-bold",
                        day_selected: "bg-blue-500/20 text-white font-bold ring-2 ring-blue-400/30",
                        day_today: "bg-white/5 text-white font-semibold",
                        day_outside: "text-white/20 opacity-50",
                        day_disabled: "!text-white/20 !opacity-30 !cursor-not-allowed hover:!bg-transparent",
                        day_hidden: "invisible"
                      }}
                    />
                  </div>
                </div>}

              {/* Section Créneaux horaires */}
              {selectedDate && !isLoadingSlots && <div ref={timeSlotsRef} className="glass rounded-3xl p-6 shadow-xl scroll-mt-6">
                  {/* Header avec retour */}
                  

                  {/* Loading overlay */}
                  {isLoadingSlots && <div className="relative py-12">
                      <div className="flex justify-center">
                        <Spinner size="md" variant="default" label="Chargement des créneaux" />
                      </div>
                    </div>}

                  {/* Grid créneaux 2 colonnes */}
                  {!isLoadingSlots && <div className="grid grid-cols-2 gap-3">
                      {availableSlots.map((slot, index) => {
                        const isSelected = selectedSlot?.start === slot.start;
                        const displayTimeRange = formatTimeRange(slot.start, slot.end);
                        return (
                          <button
                            key={`${slot.start}-${index}`}
                            onClick={() => handleSlotSelect(slot)}
                            className={cn(
                              "rounded-xl px-4 py-3 flex flex-col transition-all",
                              isSelected
                                ? "ring-2 ring-white/20 bg-white/10"
                                : "glass hover:bg-white/5 active:scale-95"
                            )}
                          >
                            <span className="text-sm text-white font-medium">{displayTimeRange}</span>
                          </button>
                        );
                      })}
                    </div>}

                  {!isLoadingSlots && availableSlots.length === 0 && <div className="text-center py-8">
                      <p className="text-white/60 text-sm">Aucun créneau disponible pour cette date</p>
                    </div>}
                </div>}
            </div>

          </div>
        </div>
      </div>

      {/* Booking Confirmation Dialog */}
      <BookingConfirmation
        isOpen={showConfirmDialog}
        selectedSlot={selectedSlot}
        selectedDate={selectedDate}
        planKey={selectedPlan}
        locationName={selectedLocationName || 'Localisation'}
        acceptedCGV={acceptedCGV}
        showCGVWarning={showCGVWarning}
        isBooking={isBooking}
        bookingWithMultiHook={bookingWithMultiHook}
        onClose={handleCloseConfirmDialog}
        onAcceptCGV={setAcceptedCGV}
        onOpenCGV={() => setShowCGVDialog(true)}
        onConfirm={handleConfirmBooking}
      />

      {/* Multi Hook Dialog */}
      <MultiHookModal
        open={showMultiHookDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowMultiHookDialog(false);
            setShowConfirmDialog(true);
          } else {
            setShowMultiHookDialog(true);
          }
        }}
        selectedPlan={selectedPlan}
        currentPlanLabel={currentPlan?.label}
        currentPlanPrice={currentPlan?.price}
        isBooking={isBooking}
        bookingWithMultiHook={bookingWithMultiHook}
        onFinalBooking={handleFinalBooking}
      />

      {/* CGV Dialog */}
      <Dialog open={showCGVDialog} onOpenChange={setShowCGVDialog}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-[70] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <DialogContent className="fixed left-1/2 top-1/2 z-[80] -translate-x-1/2 -translate-y-1/2 bg-black/95 border border-white/10 text-white max-w-[calc(100%-2rem)] backdrop-blur-xl max-h-[80vh] rounded-lg p-6 shadow-lg w-full data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
            <DialogHeader>
              <DialogTitle>Conditions Générales de Vente</DialogTitle>
            </DialogHeader>
            <DialogPrimitive.Close className="absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-0">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
            <ScrollArea className="h-[60vh] pr-4 mt-4">
            <div className="space-y-4 text-sm text-white/70">
              <h2 className="text-lg font-semibold text-white mt-4">CONDITIONS GÉNÉRALES DE VENTE AGENCE PERSONNELLE</h2>
              
              <div>
                <h3 className="text-base font-semibold text-white mb-2">ARTICLE 1 - Champ d'application</h3>
                <p className="mb-2">Les présentes Conditions Générales de Vente constituent, conformément à l'article L 441-1 du Code du Commerce, le socle unique de la relation commerciale entre les parties.</p>
                <p className="mb-2">Elles ont pour objet de définir les conditions dans lesquelles L'AGENCE PERSONNELLE (« Le Prestataire ») fournit aux Clients professionnels (« Les Clients ou le Client ») qui lui en font la demande, via le site internet du Prestataire, par contact direct ou via un support papier, les services suivants (ci-après « les Services ») :</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Création de contenu audiovisuel : production de vidéos courtes optimisées pour les réseaux sociaux, avec des pack incluant un nombre déterminé de vidéos par session, optimisées pour les plateformes comme Instagram, Facebook, TikTok, LinkedIn et YouTube.</li>
                  <li>Stratégie éditoriale et conseil en marketing digital : conception d'une ligne éditoriale sur mesure basée sur les objectifs commerciaux et le public cible de chaque Client.</li>
                  <li>Gestion des réseaux sociaux (Community Management) : prise en charge de la diffusion et du suivi des performances des vidéos publiées.</li>
                  <li>Optimisation des profils et contenu multicanal : l'AGENCE PERSONNELLE adapte les vidéos et le contenu pour qu'ils soient performants sur plusieurs plateformes en parallèle.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-base font-semibold text-white mb-2">ARTICLE 2 - Commandes</h3>
                <h4 className="font-medium text-white/90 mb-1">2-1. Processus de commande</h4>
                <p className="mb-2">Les ventes de Services ne sont parfaites qu'après l'établissement d'un devis détaillé et l'acceptation expresse et écrite de la commande du Client par le Prestataire.</p>
                
                <h4 className="font-medium text-white/90 mb-1 mt-3">2-2. Étapes des prestations</h4>
                <p className="mb-2">Les prestations commandées suivent les étapes suivantes :</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Brief initial et co-création</li>
                  <li>Préparation de la ligne éditoriale</li>
                  <li>Tournage des vidéos</li>
                  <li>Post-production et montage</li>
                  <li>Ajout de sous-titres</li>
                </ul>
                
                <h4 className="font-medium text-white/90 mb-1 mt-3">2-3. Modifications de commande</h4>
                <p className="mb-2">Toute demande de modification de commande formulée par le Client sera étudiée dans la limite des possibilités techniques et organisationnelles du Prestataire. Ces modifications devront être notifiées par écrit au moins sept (7) jours avant la date prévue du premier tournage vidéo.</p>
                
                <h4 className="font-medium text-white/90 mb-1 mt-3">2-4. Annulation de commande</h4>
                <p className="mb-2">En cas d'annulation de commande par le Client après son acceptation par le Prestataire et moins de quatorze (14) jours ouvrés avant la date prévue du tournage vidéo, une indemnité équivalente à 50% du Forfait choisi sera facturée au Client, sauf en cas de force majeure.</p>
                
                <h4 className="font-medium text-white/90 mb-1 mt-3">2-5. Report de tournage</h4>
                <p>En cas de report de tournage par le Client après son acceptation par le Prestataire et moins de sept (7) jours avant la date prévue du tournage vidéo, une indemnité équivalente à quatre cent (400) euros HT sera facturée au Client, sauf en cas de force majeure.</p>
              </div>

              <div>
                <h3 className="text-base font-semibold text-white mb-2">ARTICLE 3 - Tarifs</h3>
                <h4 className="font-medium text-white/90 mb-1">3-1. Principes généraux</h4>
                <p className="mb-2">Les prestations de Services sont fournies aux tarifs en vigueur au jour de la passation de la commande. Les tarifs s'entendent nets et hors taxes (HT).</p>
                
                <h4 className="font-medium text-white/90 mb-1 mt-3">3-2. Forfaits proposés</h4>
                <ul className="list-disc pl-5 space-y-2">
                  <li><strong>Forfait Accélération :</strong> 5 000 € HT pour 20 vidéos diffusées sur un mois. Option Multi-hook : 1 000 € HT par mois.</li>
                  <li><strong>Forfait Émergence :</strong> 2 500 € HT par mois (soit 5 000 € HT pour deux mois) pour 20 vidéos diffusées sur deux mois. Ce forfait est souscrit pour un cycle de 2 mois consécutifs, non résiliable avant l'issue de cette période. Option Multi-hook : 500 € HT par mois.</li>
                  <li><strong>Forfait Présence :</strong> 1 000 € HT par mois (soit 6 000 € HT pour 6 mois) pour 24 vidéos diffusées sur six mois. Ce forfait est souscrit pour un cycle de 6 mois consécutifs, non résiliable avant l'issue de cette période. Option Multi-hook : 200 € HT par mois.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-base font-semibold text-white mb-2">ARTICLE 4 - Conditions de règlement</h3>
                <h4 className="font-medium text-white/90 mb-1">4-1. Modalités de paiement</h4>
                <p className="mb-2">Les prestations sont facturées mensuellement, à date anniversaire du tournage. Le paiement de la facture est exigible à réception et est réalisé par prélèvement bancaire automatique.</p>
                
                <h4 className="font-medium text-white/90 mb-1 mt-3">4-2. Retard de paiement</h4>
                <p className="mb-2">En cas de retard de paiement, des pénalités de retard seront automatiquement appliquées. Le Client sera redevable de plein droit d'une indemnité forfaitaire pour frais de recouvrement d'un montant de quarante (40) euros.</p>
                
                <h4 className="font-medium text-white/90 mb-1 mt-3">4-3. Engagement, annulation ou résiliation</h4>
                <p>Tout forfait engagé est ferme et définitif. Une fois la commande validée et le tournage programmé, le Client est tenu de régler l'intégralité du forfait choisi, même en cas de désistement ou d'absence lors de la session.</p>
              </div>

              <div>
                <h3 className="text-base font-semibold text-white mb-2">ARTICLE 5 - Modalités de fourniture des Services</h3>
                <h4 className="font-medium text-white/90 mb-1">5-1. Délais de réalisation</h4>
                <p className="mb-2">Chaque session de tournage permet l'enregistrement de 20 à 24 vidéos en environ trois heures. Le processus de post-production est réalisé dans un délai pouvant aller jusqu'à trente (30) jours ouvrables à compter de la session de tournage.</p>
                
                <h4 className="font-medium text-white/90 mb-1 mt-3">5-2. Validation à chaque étape</h4>
                <p className="mb-2">Le Prestataire met en œuvre un processus de validation collaboratif avec le Client. Le client dispose d'un délai de 15 jours ouvrables après envoi pour faire part de ses commentaires dans la limite de 1 aller-retour. Tout aller-retour supplémentaire sera facturé 50€ HT au Client.</p>
                <p>Les vidéos validées par le Client pourront être diffusées au maximum 6 mois après la date du tournage.</p>
              </div>

              <div>
                <h3 className="text-base font-semibold text-white mb-2">ARTICLE 6 - Responsabilité du Prestataire - Garantie</h3>
                <p className="mb-2">La responsabilité du Prestataire ne peut être engagée qu'en cas de faute ou de négligence prouvée dans l'exécution de ses obligations contractuelles. Elle est strictement limitée aux préjudices directs subis par le Client.</p>
                <p>Le Prestataire ne saurait en aucun cas être tenu responsable du contenu des vidéos, des propos tenus ou diffusés par le Client. Le Client est seul responsable du respect du cadre légal, réglementaire et professionnel applicable à sa prise de parole publique.</p>
              </div>

              <div>
                <h3 className="text-base font-semibold text-white mb-2">ARTICLE 7 - Droit de propriété intellectuelle</h3>
                <h4 className="font-medium text-white/90 mb-1">7-1. Propriété des créations et contenus</h4>
                <p className="mb-2">Le Prestataire conserve l'intégralité des droits de propriété intellectuelle sur les créations réalisées dans le cadre de la prestation de services.</p>
                
                <h4 className="font-medium text-white/90 mb-1 mt-3">7-2. Licence d'utilisation accordée au Client</h4>
                <p>Le Prestataire accorde au Client une licence d'utilisation non exclusive, mondiale, et limitée à l'usage professionnel du Client sur les supports et plateformes convenus. Cette licence couvre uniquement les vidéos finales validées et livrées.</p>
              </div>

              <div>
                <h3 className="text-base font-semibold text-white mb-2">ARTICLE 8 - Données personnelles</h3>
                <p className="mb-2">Les données personnelles recueillies auprès des Clients font l'objet d'un traitement informatique réalisé par le Prestataire. Le responsable du traitement des données est l'AGENCE PERSONNELLE.</p>
                <p className="mb-2">Conformément à la réglementation en vigueur, le Client dispose des droits suivants : droit d'accès, droit de rectification, droit d'effacement, droit à la portabilité, droit de limitation ou d'opposition au traitement pour motif légitime.</p>
                <p>Ces droits peuvent être exercés en contactant le responsable de traitement par email à admin@agencepersonnelle.com ou par courrier postal à AGENCE PERSONNELLE : 9 rue des Colonnes – 75020 PARIS.</p>
              </div>

              <div>
                <h3 className="text-base font-semibold text-white mb-2">ARTICLE 9 à 16 - Dispositions générales</h3>
                <p className="mb-2">Les présentes Conditions générales incluent également les dispositions relatives à l'imprévision, l'exécution forcée en nature, l'exception d'inexécution, la force majeure, la résolution du contrat, les litiges, la langue du contrat et le droit applicable.</p>
                <p>Tous les litiges seront soumis à la compétence exclusive du Tribunal de commerce de Paris. Les présentes Conditions Générales sont régies par le droit français.</p>
              </div>
            </div>
          </ScrollArea>
          </DialogContent>
      </DialogPrimitive.Portal>
    </Dialog>

      {/* Preparation Booking Screen - S'affiche AVANT BookingSuccess si multi-hook */}
      {showPreparationBooking && selectedSlot && productId && (
        <PreparationBookingScreen 
          open={showPreparationBooking}
          onClose={() => setShowPreparationBooking(false)}
          shootingDate={new Date(selectedSlot.start)}
          userName={userName}
          userEmail={userEmail}
          productId={productId}
          shootingId={shootingId}
          onBookingSuccess={handlePreparationBookingSuccess}
        />
      )}

      {/* Booking Success Screen - S'affiche soit après PreparationBooking, soit directement */}
      <BookingSuccessScreen 
        open={showBookingSuccess}
        onClose={handleBookingSuccessClose}
        shootingDate={confirmedShootingDate}
        preparationDate={confirmedPreparationDate}
        directorName={directorName}
        directorAvatar={directorAvatar}
        pbmName={pbmName}
        pbmAvatar={pbmAvatar}
      />
    </div>;
}